import { useState } from "react";
import { downloadExcel } from "../services/api";

export default function Results({ data }) {
  const [activeTab, setActiveTab] = useState("testcases");

  if (!data) return null;

  const testCases = Array.isArray(data.test_cases)
    ? data.test_cases
    : [];

  const coverage = data.coverage || {};

  const getColor = (score) => {
    if (score > 80) return "bg-green-500";
    if (score > 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  const handleDownload = async () => {
    try {
      const res = await downloadExcel(testCases);

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "testcases.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  return (
    <div className="max-w-5xl mx-auto mt-6">

            {data?.usage && (
        <div className="text-sm text-gray-600 mb-2">
          Usage: {data.usage.used} / {data.usage.limit}
        </div>
      )}

      {/* ✅ Context */}
      {data.used_context && (
        <p className="text-green-600 mb-2">
          Context-aware results used 📚
        </p>
      )}

      {/* ===================== */}
      {/* 🔥 TABS HEADER */}
      {/* ===================== */}
      <div className="flex gap-2 mb-4 border-b pb-2">
        {[
          { key: "testcases", label: "Test Cases" },
          { key: "coverage", label: "Coverage" },
          { key: "missing", label: "Missing" },
          { key: "risks", label: "Risks" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-t ${
              activeTab === tab.key
                ? "bg-indigo-600 text-white"
                : "bg-gray-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===================== */}
      {/* 🧪 TEST CASES TAB */}
      {/* ===================== */}
      {activeTab === "testcases" && (
        <div className="bg-white p-4 rounded-xl shadow border">

          {testCases.length > 0 && (
            <div className="text-right mb-3">
              <button
                onClick={handleDownload}
                className="bg-indigo-600 text-white px-4 py-2 rounded"
              >
                Download Excel
              </button>
            </div>
          )}

          {testCases.length === 0 ? (
            <pre>{data.test_cases}</pre>
          ) : (
            testCases.map((tc, index) => (
              <div key={index} className="border p-3 mb-3 rounded bg-gray-50">
                <p><strong>{tc.id}</strong> - {tc.type}</p>
                <p>{tc.description}</p>
                <p><strong>Steps:</strong> {tc.steps}</p>
                <p><strong>Expected:</strong> {tc.expected}</p>
                <p><strong>Priority:</strong> {tc.priority}</p>
              </div>
            ))
          )}
        </div>
      )}

      {/* ===================== */}
      {/* 📊 COVERAGE TAB */}
      {/* ===================== */}
      {activeTab === "coverage" && (
        <div className="bg-blue-50 p-5 rounded-xl shadow border">

          <h2 className="text-xl font-semibold mb-3 text-blue-700">
            Coverage Analysis
          </h2>

          {coverage.coverage_score !== undefined ? (
            <>
              <div className="mb-3">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full ${getColor(coverage.coverage_score)}`}
                    style={{ width: `${coverage.coverage_score}%` }}
                  ></div>
                </div>
                <p className="mt-1 text-sm">
                  Coverage Score: {coverage.coverage_score}%
                </p>
              </div>

              <p>
                <b>Risk Level:</b> {coverage.risk_level}
              </p>

              <div className="mt-3">
                <b>Covered:</b>
                <ul className="list-disc ml-5 text-green-700">
                  {coverage.covered_areas?.map((i, idx) => (
                    <li key={idx}>{i}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-3">
                <b>Missing:</b>
                <ul className="list-disc ml-5 text-red-600">
                  {coverage.missing_areas?.map((i, idx) => (
                    <li key={idx}>{i}</li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <p>No coverage data</p>
          )}
        </div>
      )}

      {/* ===================== */}
      {/* ⚠️ MISSING TAB */}
      {/* ===================== */}
      {activeTab === "missing" && (
        <div className="bg-yellow-100 p-4 rounded-xl shadow">
          <h2 className="font-semibold mb-2">Missing Scenarios</h2>
          <pre className="whitespace-pre-wrap">{data.missing}</pre>
        </div>
      )}

      {/* ===================== */}
      {/* 🚨 RISKS TAB */}
      {/* ===================== */}
      {activeTab === "risks" && (
        <div className="bg-red-100 p-4 rounded-xl shadow">
          <h2 className="font-semibold mb-2">Risks</h2>
          <pre className="whitespace-pre-wrap">{data.risks}</pre>
        </div>
      )}

    </div>
  );
}